import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Save, FileCheck, FileText, Download, Sparkles, Lock, Unlock, AlertTriangle, Plus, X, Wand2, ChevronDown, ChevronRight, ChevronLeft, ClipboardList, Eye, Play } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatErr } from "../lib/api";

/* Wave V.4 — Design Basis Wizard
 * Schema-driven, per-sub-stage form for A.1..A.5 (process_plant only).
 * The schema arrives from /api/projects/{pid}/design-basis/schema/{code}.
 * The Documents sub-tab on each A.x stage panel mounts this component.
 */
export default function DesignBasisWizard({ projectId, stageCode }) {
  const [schema, setSchema] = useState(null);
  const [title, setTitle] = useState("");
  const [data, setData] = useState({});
  const [provenance, setProvenance] = useState({});
  const [completeness, setCompleteness] = useState(null);
  const [locked, setLocked] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [downloads, setDownloads] = useState(null);
  const [quickPrompt, setQuickPrompt] = useState("");
  const [showQuick, setShowQuick] = useState(false);
  const [revision, setRevision] = useState(null);
  const [cities, setCities] = useState([]);       // legacy flat list
  const [citiesRich, setCitiesRich] = useState([]); // [{name,state,country_code,lat,lon,...}]
  // Wave V.6 — Revise with AI
  const [reviseInstruction, setReviseInstruction] = useState("");
  // CH.3 — Revise with AI panel collapsible (persists per session).
  const [reviseCollapsed, setReviseCollapsed] = useState(() => {
    try { return sessionStorage.getItem("rachana.reviseCollapsed") === "1"; }
    catch { return false; }
  });
  // Wave V.7.2 — Revise-with-AI enrichments: section reference, screenshots,
  // version history
  const [reviseSectionRef, setReviseSectionRef] = useState("");
  const [reviseScreenshots, setReviseScreenshots] = useState([]);
  const [versions, setVersions] = useState([]);
  // Wave V.7 — two-tab layout: "form" (collapsible sections) vs "document"
  // (generated narrative + downloads + revise). Replaces the old horizontal
  // section-tab row with a single vertical accordion on the Form tab.
  const [viewMode, setViewMode] = useState("form");
  const [narrativeMd, setNarrativeMd] = useState("");
  const [latestDeliverable, setLatestDeliverable] = useState(null);  // Wave AL.2 — server-side latest pointer
  // Which accordion sections are open. Default: the first is expanded so
  // the user isn't staring at five closed panes on first load.
  const [openSections, setOpenSections] = useState(() => new Set());

  const apiRoot = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  const refresh = useCallback(async () => {
    try {
      setErr("");
      const sch = await api.get(`/projects/${projectId}/design-basis/schema/${stageCode}`);
      setSchema(sch.data.schema);
      setTitle(sch.data.title);
      const wd = await api.get(`/projects/${projectId}/design-basis/wizard-data/${stageCode}`);
      setData(wd.data.data || {});
      setProvenance(wd.data._provenance || {});
      setCompleteness(wd.data.completeness);
      setLocked(!!wd.data.locked);
      setRevision(typeof wd.data.revision === "number" ? wd.data.revision : 0);
      // Wave V.6 — if a narrative already exists, surface the Downloads &
      // Revise panels so the user can re-download or iterate immediately.
      if (wd.data.narrative_markdown) {
        setNarrativeMd(wd.data.narrative_markdown);
        setDownloads({
          pdf:  `/projects/${projectId}/design-basis/document/${stageCode}/pdf`,
          docx: `/projects/${projectId}/design-basis/document/${stageCode}/docx`,
          json: `/projects/${projectId}/design-basis/document/${stageCode}/json`,
        });
      }
      // Wave AL.2 — also fetch the server's latest deliverable pointer so
      // the Generated Document tab can show downloads even when the AI
      // narrative is empty (deliverable was rendered as a structured-only
      // fallback).
      try {
        const ld = await api.get(
          `/projects/${projectId}/design-basis/document/${stageCode}/latest`
        );
        setLatestDeliverable(ld.data || null);
      } catch (e) {
        if (e.response?.status !== 404) console.warn("latest deliverable:", e);
        setLatestDeliverable(null);
      }
      // Wave V.7 — open first accordion section by default on initial load.
      if (sch.data.schema?.length) {
        setOpenSections((cur) => (cur.size === 0
          ? new Set([sch.data.schema[0].id])
          : cur));
      }
      if (sch.data.schema?.length && !activeTab) setActiveTab(sch.data.schema[0].id);
      // Lazy-load city list once
      if (cities.length === 0) {
        try {
          const c = await api.get(`/projects/${projectId}/design-basis/cities`);
          setCities(c.data?.cities || []);
          setCitiesRich(c.data?.cities_rich || []);
        } catch { /* ignore */ }
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Load failed");
    }
  }, [projectId, stageCode, activeTab, cities.length]);

  useEffect(() => {
    // Reset per-stage state when switching between A.1/A.2/.. so that
    // autosave doesn't fire with a stale `data={}` from the previous stage.
    setSchema(null);
    setData({});
    setRevision(null);
    setLocked(false);
    setErr("");
    setInfo("");
    setDownloads(null);
    setNarrativeMd("");
    setLatestDeliverable(null);
    setOpenSections(new Set());
    setViewMode("form");
    setReviseInstruction("");
    setReviseSectionRef("");
    setReviseScreenshots([]);
    setVersions([]);
    refresh();
    /* eslint-disable-next-line */
  }, [projectId, stageCode]);

  // Autosave (debounced). Writes every 1.2 s after the last keystroke.
  //
  // Save behaviour notes:
  //  • On success we clear any previous banner so stale 'Save failed' text
  //    doesn't linger after a later write succeeds.
  //  • On 401/403 we surface the HTTP status so the user can actually tell
  //    "you're signed out / no permission" apart from generic network issues
  //    and reload/re-login rather than staring at a dead banner.
  //  • The initial mount effect (line ~55) kicks this off with `data={}` on
  //    the first render; we gate on schema load + revision pre-fetch to
  //    avoid clobbering server state with an empty payload (see guard below).
  const [saveFailedOnce, setSaveFailedOnce] = useState(false);
  useEffect(() => {
    if (!schema || locked) return;
    // Guard — don't autosave until the server data has been fetched at
    // least once. Before the first GET /wizard-data response, `data` is
    // {} which, if PUTed, would wipe the server copy.
    if (revision == null) return;
    const t = setTimeout(async () => {
      try {
        setSaving(true);
        const r = await api.put(`/projects/${projectId}/design-basis/wizard-data/${stageCode}`, { data });
        setCompleteness(r.data.completeness);
        setProvenance(r.data._provenance || {});
        if (typeof r.data.revision === "number") setRevision(r.data.revision);
        // Any prior save error is now invalidated by a successful write
        if (saveFailedOnce) { setErr(""); setSaveFailedOnce(false); }
      } catch (e) {
        const status = e.response?.status;
        const detail = formatErr(e.response?.data?.detail);
        let msg = detail || e.message || "Save failed";
        if (status === 401) msg = "Your session has expired — please reload and sign in again to keep editing.";
        else if (status === 403) msg = "You don't have edit permission on this project. (403)";
        else if (status === 409) msg = detail || "The wizard is locked (already approved). Click Revise to edit.";
        else if (status >= 500) msg = `Server error while saving (HTTP ${status}). Your draft is still in the browser — try again in a moment.`;
        setErr(msg);
        setSaveFailedOnce(true);
      } finally {
        setSaving(false);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [data, schema, locked, projectId, stageCode, revision, saveFailedOnce]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  /** Wave V.6 — manual "Save" button. Forces an immediate PUT, flashes a
   * green confirmation, and emits the same status-specific error taxonomy
   * as the autosave loop (401/403/409/5xx). */
  const saveNow = async () => {
    if (locked) return;
    setBusyAction("save"); setErr(""); setInfo("");
    try {
      const r = await api.put(
        `/projects/${projectId}/design-basis/wizard-data/${stageCode}`,
        { data }
      );
      setCompleteness(r.data.completeness);
      setProvenance(r.data._provenance || {});
      if (typeof r.data.revision === "number") setRevision(r.data.revision);
      setSaveFailedOnce(false);
      setInfo(`Saved — revision ${r.data.revision}.`);
    } catch (e) {
      const status = e.response?.status;
      const detail = formatErr(e.response?.data?.detail);
      let msg = detail || e.message || "Save failed";
      if (status === 401) msg = "Your session has expired — please reload and sign in again.";
      else if (status === 403) msg = "You don't have edit permission on this project. (403)";
      else if (status === 409) msg = detail || "Wizard is locked (already approved). Click Revise to edit.";
      else if (status >= 500) msg = `Server error while saving (HTTP ${status}).`;
      setErr(msg);
      setSaveFailedOnce(true);
    } finally { setBusyAction(null); }
  };

  const applyLocationDefaults = async (cityOrRecord) => {
    if (!cityOrRecord) return;
    // Accept either a plain city name (from typing) or a rich record
    // returned by the geocode endpoint / autocomplete pick.
    const isRecord = typeof cityOrRecord === "object" && cityOrRecord;
    const city = isRecord
      ? (cityOrRecord.name || cityOrRecord.label || "")
      : String(cityOrRecord);
    if (!city) return;
    try {
      let defaults = {};
      if (isRecord && cityOrRecord.defaults
          && Object.keys(cityOrRecord.defaults).length) {
        defaults = cityOrRecord.defaults;
      } else {
        const r = await api.get(`/projects/${projectId}/design-basis/defaults/${encodeURIComponent(city)}`);
        defaults = r.data?.defaults || {};
      }
      if (Object.keys(defaults).length) {
        // Preserve any user edits — only fill blank fields.
        setData((d) => {
          const merged = { ...d, location: city };
          for (const [k, v] of Object.entries(defaults)) {
            if (merged[k] == null || merged[k] === "" ||
                (Array.isArray(merged[k]) && merged[k].length === 0)) {
              merged[k] = v;
            }
          }
          return merged;
        });
        // Provenance markers so the UI shows the "auto-filled" badge
        setProvenance((p) => ({
          ...p,
          ...Object.fromEntries(Object.keys(defaults).map((k) => [k, "auto_filled"])),
          location: "auto_filled",
        }));
        setInfo(`Auto-filled ${Object.keys(defaults).length} fields from ${city}${
          isRecord && cityOrRecord.state ? `, ${cityOrRecord.state}` : ""
        }${isRecord && cityOrRecord.country_code ? `, ${cityOrRecord.country_code}` : ""}.`);
      } else {
        // Location is set but no defaults known — still ensure `location`
        // is saved so the completeness alarm clears.
        setData((d) => ({ ...d, location: city }));
      }
    } catch {
      // Ensure at minimum the location itself is set even if the
      // defaults call fails, so "missing location" clears.
      setData((d) => ({ ...d, location: city }));
    }
  };

  // Wave AK — Wizard Excel template download / export / import
  const downloadXlsx = async (mode /* "template" | "export" */) => {
    setBusyAction(mode === "template" ? "xlsx-tpl" : "xlsx-exp");
    setErr(""); setInfo("");
    try {
      const r = await api.get(
        `/projects/${projectId}/wizard-excel/${stageCode}/${mode}`,
        { responseType: "blob" });
      const blob = new Blob([r.data],
        { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Capexara Process Industry_${stageCode}_${mode === "template" ? "template" : "filled"}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setInfo(mode === "template"
        ? "Downloaded blank Excel template."
        : "Downloaded filled form as .xlsx.");
    } catch (e) {
      setErr(`Excel download failed — ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyAction(""); }
  };

  const importXlsx = async (file) => {
    setBusyAction("xlsx-imp"); setErr(""); setInfo("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/projects/${projectId}/wizard-excel/${stageCode}/import`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInfo(`Imported ${r.data.field_count} fields from ${file.name}. Reloading…`);
      // re-fetch the wizard data so the UI shows the merged result
      try {
        const fresh = await api.get(
          `/projects/${projectId}/design-basis/wizard-data/${stageCode}`);
        const fd2 = fresh.data?.data || {};
        setData(fd2);
        setRevision(fresh.data?.revision || 0);
      } catch { /* fallback: full reload */ window.location.reload(); }
    } catch (e) {
      setErr(`Excel import failed — ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyAction(""); }
  };

  const uploadFile = async (fieldKey, file) => {
    if (!file) return;
    setBusyAction(`upload-${fieldKey}`); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/projects/${projectId}/design-basis/upload/${stageCode}/${fieldKey}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      setData(r.data.data || {});
      setProvenance(r.data._provenance || {});
      setInfo(`Uploaded ${file.name}.`);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Upload failed");
    } finally { setBusyAction(null); }
  };

  const removeUpload = async (fieldKey, gridfsId) => {
    setBusyAction(`remove-${gridfsId}`); setErr("");
    try {
      const r = await api.delete(
        `/projects/${projectId}/design-basis/upload/${stageCode}/${fieldKey}/${gridfsId}`,
      );
      setData(r.data.data || {});
      setProvenance(r.data._provenance || {});
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Delete failed");
    } finally { setBusyAction(null); }
  };

  const generate = async (opts = {}) => {
    const forceRegenerate = !!opts.forceRegenerate;
    setBusyAction("generate"); setErr(""); setInfo("");
    setInfo(forceRegenerate
      ? "Capexara Process Industry AI is drafting a fresh document (this call charges Capexara Process Industry AI) …"
      : "Capexara Process Industry AI is preparing the document — cached narrative will be reused if available …");
    try {
      // Kick off the background job (returns immediately with job_id)
      const kick = await api.post(
        `/projects/${projectId}/design-basis/generate-document/${stageCode}` +
          (forceRegenerate ? "?force_regenerate=true" : "")
      );
      const jobId = kick.data?.job_id;
      const willCallLLM = !!kick.data?.will_call_llm;
      if (!jobId) throw new Error("No job id returned from generate endpoint");
      setInfo(willCallLLM
        ? "Calling Capexara Process Industry AI — generating in parallel sections, this will consume Capexara Process Industry AI credits …"
        : "Reusing cached narrative — no Capexara Process Industry AI charges this time …");
      // Wave AN — poll every 3 s for up to 15 minutes (300 ticks). The
      // backend now streams per-section progress every 30-60 s, so the
      // user always sees forward motion and we never give up too early.
      const poll = async () => {
        for (let i = 0; i < 300; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const s = await api.get(
              `/projects/${projectId}/design-basis/generate-document/${stageCode}/status/${jobId}`
            );
            const job = s.data || {};
            if (job.status === "complete") {
              setDownloads(job.downloads || null);
              const sectionsBit = job.failed_section_count
                ? ` · ${job.ok_section_count}/${(job.ok_section_count || 0) + (job.failed_section_count || 0)} sections OK`
                : (job.ok_section_count ? ` · ${job.ok_section_count} sections` : "");
              const authored = job.narrative_generated
                ? ` (narrative by Capexara Process Industry AI · ${job.narrative_chars} chars${sectionsBit}${
                    job.llm_called ? "" : " · reused from cache — no Capexara Process Industry AI charges"
                  })`
                : " (structured field dump — AI narrative unavailable)";
              setInfo(`Generated ${title} v${job.version}${authored}.`);
              // Wave V.7 — pull fresh narrative + flip to the Document tab.
              try {
                const wd = await api.get(`/projects/${projectId}/design-basis/wizard-data/${stageCode}`);
                if (wd.data?.narrative_markdown) setNarrativeMd(wd.data.narrative_markdown);
              } catch { /* ignore */ }
              // Wave AL.2 — refresh the latest deliverable pointer too
              try {
                const ld = await api.get(`/projects/${projectId}/design-basis/document/${stageCode}/latest`);
                setLatestDeliverable(ld.data || null);
              } catch { /* ignore */ }
              setViewMode("document");
              return;
            }
            if (job.status === "failed") {
              // Wave AL — distinguish LLM charges from non-LLM failures.
              const charged = job.llm_called;
              const preserved = job.narrative_preserved
                ? " Your cached narrative is preserved — click \"Retry rendering\" to rebuild the PDF without re-calling Capexara Process Industry AI."
                : "";
              const msg = `${job.error || "Generation failed"} · ${
                charged
                  ? "⚠ Capexara Process Industry AI call was made (credits may have been consumed)"
                  : "✓ No Capexara Process Industry AI call was made — you were NOT charged"
              }${preserved}`;
              throw new Error(msg);
            }
            // still running — surface the current step
            const prog = job.progress || {};
            let human;
            if (prog.idx && prog.total) {
              const pct = Math.round((prog.idx / prog.total) * 100);
              human = `Section ${prog.idx}/${prog.total} (${pct}%) — ${prog.section_title || job.step}`;
            } else {
              human = {
                drafting: "Preparing context…",
                using_cached_narrative: "Using cached narrative (no Capexara Process Industry AI charge) …",
                reusing_cached_narrative: "Reusing cached narrative (no Capexara Process Industry AI charge) …",
                claude_opus_4_7_drafting: "Capexara Process Industry AI is drafting (Capexara Process Industry AI) …",
                rendering_pdf_docx_json: "Rendering PDF / DOCX / JSON…",
                llm_returned_empty: "LLM returned no content — rendering structured document only …",
              }[job.step] || `Working (${job.step})…`;
            }
            setInfo(human);
          } catch (inner) {
            if (inner.response?.status === 404) {
              // Transient — keep polling
              continue;
            }
            throw inner;
          }
        }
        throw new Error("Generation timed out after 15 minutes.");
      };
      await poll();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message || "Generation failed");
    } finally { setBusyAction(null); }
  };

  // Wave AL — "Retry rendering" reuses the cached narrative (no Capexara Process Industry AI charge)
  const retryRender = () => generate({ forceRegenerate: false });
  const regenerateWithLLM = () => {
    if (!window.confirm(
      "This will call Capexara Process Industry AI again and consume Capexara Process Industry AI credits from your account. " +
      "If you just want to re-download the PDF/DOCX, click Cancel and use \"Retry rendering\" instead."
    )) return;
    generate({ forceRegenerate: true });
  };

  const approve = async () => {
    setBusyAction("approve"); setErr("");
    try {
      const r = await api.post(`/projects/${projectId}/design-basis/approve/${stageCode}`);
      const nextList = r?.data?.next_unblocked || [];
      if (nextList.length) {
        const codes = nextList.map((s) => s.code).join(", ");
        setInfo(`Approved. Wizard is now locked. Unblocked: ${codes}.`);
        // Emit activation signal — StageWorkspaceTab listens and can navigate
        // on user click. We auto-navigate to the first unblocked leaf after 600ms.
        setTimeout(() => {
          try {
            window.dispatchEvent(new CustomEvent("rachana.stage.activate", { detail: { code: nextList[0].code } }));
          } catch {}
        }, 600);
      } else {
        setInfo("Approved. Wizard is now locked.");
      }
      await refresh();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Approve failed");
    } finally { setBusyAction(null); }
  };

  const reviseWithAI = async () => {
    const instr = reviseInstruction.trim();
    if (!instr) return;
    setBusyAction("revise"); setErr(""); setInfo("Sending your instruction to Capexara Process Industry AI…");
    try {
      const kick = await api.post(
        `/projects/${projectId}/design-basis/revise-narrative/${stageCode}`,
        {
          instruction: instr,
          section_ref: reviseSectionRef || null,
          screenshot_ids: reviseScreenshots.map((s) => s.id),
        },
      );
      const jobId = kick.data?.job_id;
      if (!jobId) throw new Error("No job id returned from revise endpoint");
      // Reuse the generate-status poll URL (same job store)
      for (let i = 0; i < 300; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const s = await api.get(
            `/projects/${projectId}/design-basis/generate-document/${stageCode}/status/${jobId}`
          );
          const job = s.data || {};
          if (job.status === "complete") {
            setDownloads(job.downloads || null);
            setInfo(`Revised ${title} v${job.version} — ${job.narrative_chars} chars regenerated.`);
            setReviseInstruction("");
            setReviseSectionRef("");
            setReviseScreenshots([]);
            // Wave V.7 — pull the revised narrative so the viewer updates.
            try {
              const wd = await api.get(`/projects/${projectId}/design-basis/wizard-data/${stageCode}`);
              if (wd.data?.narrative_markdown) setNarrativeMd(wd.data.narrative_markdown);
            } catch { /* ignore */ }
            refreshVersions();
            return;
          }
          if (job.status === "failed") {
            throw new Error(job.error || "Revision failed");
          }
          const human = {
            revise_queued: "Queued…",
            claude_opus_4_7_revising: "Capexara Process Industry AI is revising…",
            claude_opus_4_6_revising: "Capexara Process Industry AI is revising (Capexara Process Industry AI)…",
            rendering_pdf_docx_json: "Re-rendering PDF / DOCX / JSON…",
          }[job.step] || `Working (${job.step})…`;
          setInfo(human);
        } catch (inner) {
          if (inner.response?.status === 404) continue;
          throw inner;
        }
      }
      throw new Error("Revision timed out after 15 minutes.");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message || "Revision failed");
    } finally { setBusyAction(null); }
  };

  // Wave V.7.2 — section headings extracted from the narrative for the
  // Revise panel's "Reference section" dropdown. H1/H2/H3 lines only.
  const narrativeSections = React.useMemo(() => {
    if (!narrativeMd) return [];
    const out = [];
    for (const raw of narrativeMd.split("\n")) {
      const m = raw.match(/^(#{1,3})\s+(.+?)\s*$/);
      if (m) out.push({ level: m[1].length, title: m[2].replace(/\*+/g, "").trim() });
      if (out.length >= 40) break;
    }
    return out;
  }, [narrativeMd]);

  const uploadReviseScreenshot = async (file) => {
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(
        `/projects/${projectId}/design-basis/revise-screenshot/${stageCode}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setReviseScreenshots((prev) => [...prev, r.data]);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Screenshot upload failed");
    }
  };

  const refreshVersions = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/design-basis/versions/${stageCode}`);
      setVersions(r.data || []);
    } catch { /* ignore */ }
  }, [projectId, stageCode]);

  const restoreVersion = async (deliverableId) => {
    if (!window.confirm("Restore this version as the active narrative? A new version will be created so nothing is lost.")) return;
    setBusyAction("restore"); setErr(""); setInfo("Restoring version…");
    try {
      const r = await api.post(
        `/projects/${projectId}/design-basis/restore-version/${stageCode}/${deliverableId}`
      );
      setInfo(`Restored. New active version: v${r.data.new_version} (from v${r.data.restored_from}).`);
      const wd = await api.get(`/projects/${projectId}/design-basis/wizard-data/${stageCode}`);
      if (wd.data?.narrative_markdown) setNarrativeMd(wd.data.narrative_markdown);
      refreshVersions();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message || "Restore failed");
    } finally { setBusyAction(null); }
  };

  useEffect(() => {
    if (narrativeMd) refreshVersions();
  }, [narrativeMd, refreshVersions]);

  const revise = async () => {
    setBusyAction("revise"); setErr("");
    try {
      await api.post(`/projects/${projectId}/design-basis/revise/${stageCode}`);
      setInfo("Revised — wizard unlocked for editing.");
      await refresh();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Revise failed");
    } finally { setBusyAction(null); }
  };

  const parseQuick = async () => {
    if (!quickPrompt.trim()) return;
    setBusyAction("quick"); setErr("");
    try {
      const r = await api.post(`/projects/${projectId}/design-basis/parse-prompt/${stageCode}`, { prompt: quickPrompt });
      const extracted = r.data?.extracted || {};
      if (!Object.keys(extracted).length) {
        setErr("No fields could be extracted from the prompt. Try adding more specific details.");
      } else {
        // Backend already persisted with ai_extracted provenance; refresh to pick it up.
        await refresh();
        setInfo(`Extracted ${r.data.field_count} fields. Green-flagged values came from your prompt — review and edit as needed.`);
        setShowQuick(false);
        setQuickPrompt("");
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Parse failed");
    } finally { setBusyAction(null); }
  };

  if (!schema) {
    // If we already errored out (e.g. 404 for stages without a wizard
    // schema yet), show the error message instead of an infinite
    // spinner. The bug used to trap A.6 users in a permanent
    // "Loading wizard…" state because the catch set `err` but
    // `schema` stayed null.
    if (err) {
      return (
        <div className="pf-card" style={{ padding: 16, color: "var(--pf-text-muted)" }}>
          <div style={{ fontSize: 13, marginBottom: 4 }}>
            <AlertTriangle size={14} style={{ verticalAlign: "-2px",
                marginRight: 6, color: "var(--pf-spark)" }} />
            No interactive wizard configured for{" "}
            <strong style={{ color: "var(--pf-text)" }}>{stageCode}</strong> yet.
          </div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            Use the stage-level Run button (or the Equipment Layout
            editor tab if present) to generate this stage's output.
          </div>
        </div>
      );
    }
    return (
      <div style={{ padding: 16, color: "var(--pf-text-muted)" }}>
        <Loader2 className="pf-spin" size={14} /> Loading wizard…
      </div>
    );
  }

  const tab = schema.find((t) => t.id === activeTab) || schema[0];

  return (
    <div data-testid={`dbw-${stageCode}`} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header row */}
      <div className="pf-card" style={{ padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="pf-overline">DESIGN BASIS WIZARD</div>
          <div className="pf-heading" style={{ fontSize: 15, margin: 0 }}>{title} <span style={{ color: "var(--pf-text-muted)", fontSize: 12, marginLeft: 8 }}>{stageCode}</span></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {completeness && (
            <div data-testid={`dbw-${stageCode}-completeness`} style={{ fontSize: 12, color: completeness.percentage >= 70 ? "var(--pf-ok, #4ade80)" : "var(--pf-spark)" }}>
              {completeness.percentage}% complete · {completeness.critical_filled}/{completeness.critical_total} critical · rev {revision}
            </div>
          )}
          {saving && <span style={{ fontSize: 11, color: "var(--pf-text-muted)" }}><Loader2 className="pf-spin" size={11} /> Saving…</span>}
          {locked ? (
            <span className="pf-sev pf-sev-ok" style={{ fontSize: 11, padding: "2px 8px" }} data-testid={`dbw-${stageCode}-locked`}><Lock size={11} /> Approved</span>
          ) : null}
          {/* Wave V.6 — manual save button alongside autosave. Useful on
              flaky networks where the debounced autosave cannot confirm and
              the user wants an explicit "saved ✓" receipt before leaving. */}
          <button
            className="btn btn-primary"
            onClick={saveNow}
            disabled={locked || saving || busyAction === "save"}
            data-testid={`dbw-${stageCode}-save`}
            style={{ fontSize: 12, fontWeight: 600 }}
            title="Write the form to the server right now and show a confirmation"
          >
            <Save size={13} /> {busyAction === "save" ? "Saving…" : "Save"}
          </button>
          {/* Wave AK — Excel template round-trip */}
          <button
            className="btn btn-ghost"
            onClick={() => downloadXlsx("template")}
            disabled={busyAction === "xlsx-tpl"}
            data-testid={`dbw-${stageCode}-xlsx-template`}
            style={{ fontSize: 12 }}
            title="Download a blank .xlsx template for this stage"
          ><FileText size={13} /> {busyAction === "xlsx-tpl" ? "…" : "Template .xlsx"}</button>
          <button
            className="btn btn-ghost"
            onClick={() => downloadXlsx("export")}
            disabled={busyAction === "xlsx-exp"}
            data-testid={`dbw-${stageCode}-xlsx-export`}
            style={{ fontSize: 12 }}
            title="Download the current form data as .xlsx"
          ><Download size={13} /> {busyAction === "xlsx-exp" ? "…" : "Export .xlsx"}</button>
          <label
            className="btn btn-ghost"
            data-testid={`dbw-${stageCode}-xlsx-import-label`}
            style={{ fontSize: 12, cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.4 : 1 }}
            title="Upload a filled .xlsx — fields will be merged into the form"
          >
            <Plus size={13} /> {busyAction === "xlsx-imp" ? "Importing…" : "Import .xlsx"}
            <input type="file" accept=".xlsx" hidden disabled={locked || busyAction === "xlsx-imp"}
                   data-testid={`dbw-${stageCode}-xlsx-import`}
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) importXlsx(f); e.target.value = ""; }} />
          </label>
          <button className="btn btn-ghost" onClick={() => setShowQuick((s) => !s)} disabled={locked} data-testid={`dbw-${stageCode}-quick-toggle`} style={{ fontSize: 12 }}>
            <Wand2 size={13} /> Quick mode
          </button>
        </div>
      </div>

      {/* Quick mode panel */}
      {showQuick && (
        <div className="pf-card" style={{ padding: 12 }} data-testid={`dbw-${stageCode}-quick`}>
          <div className="pf-overline" style={{ marginBottom: 6 }}>QUICK MODE — paste a project brief and AI will pre-fill the form</div>
          <textarea
            className="pf-input pf-textarea"
            rows={4}
            value={quickPrompt}
            onChange={(e) => setQuickPrompt(e.target.value)}
            placeholder='e.g., "100 MTPD Crude Distillation Unit in Jamnagar. Arabian Light crude, API 33.4, sulfur 1.8%. Products: LPG, Naphtha, Kero, Diesel, RCO. FEED level. IS/API codes."'
            data-testid={`dbw-${stageCode}-quick-input`}
            disabled={locked || busyAction === "quick"}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8, gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setShowQuick(false)} disabled={busyAction === "quick"}>Cancel</button>
            <button className="btn btn-primary" onClick={parseQuick} disabled={!quickPrompt.trim() || busyAction === "quick"} data-testid={`dbw-${stageCode}-quick-parse`}>
              {busyAction === "quick" ? <><Loader2 className="pf-spin" size={13} /> Parsing…</> : <><Sparkles size={13} /> Pre-fill from prompt</>}
            </button>
          </div>
        </div>
      )}

      {err && <div className="pf-sev pf-sev-error" data-testid={`dbw-${stageCode}-err`}>{err}</div>}
      {info && <div className="pf-sev pf-sev-ok" data-testid={`dbw-${stageCode}-info`}>{info}</div>}

      {/* Wave V.7 — two top-level tabs: Form · Generated Document ────────*/}
      <div
        data-testid={`dbw-${stageCode}-view-tabs`}
        style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--pf-border)" }}
      >
        {[
          { id: "form", label: "Form", Icon: ClipboardList },
          { id: "document", label: "Generated Document", Icon: Eye },
        ].map(({ id, label, Icon }) => {
          const active = viewMode === id;
          const disabled = id === "document" && !narrativeMd && !latestDeliverable;
          return (
            <button
              key={id}
              type="button"
              onClick={() => !disabled && setViewMode(id)}
              disabled={disabled}
              data-testid={`dbw-${stageCode}-view-${id}`}
              style={{
                background: "transparent", border: "none",
                padding: "10px 16px",
                cursor: disabled ? "not-allowed" : "pointer",
                color: active ? "var(--pf-text)" : (disabled ? "var(--pf-text-dim)" : "var(--pf-text-muted)"),
                borderBottom: active ? "2px solid var(--pf-spark)" : "2px solid transparent",
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                display: "flex", alignItems: "center", gap: 6,
              }}
              title={disabled ? "Click Generate Document below to create the document." : ""}
            >
              <Icon size={13} /> {label}
              {id === "document" && narrativeMd && (
                <span
                  style={{
                    marginLeft: 6, fontSize: 10,
                    color: "var(--pf-ok, #4ade80)",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  · {(narrativeMd.length / 1000).toFixed(1)}k chars
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── FORM TAB · collapsible accordion of sections ──────────────── */}
      {viewMode === "form" && (
        <div data-testid={`dbw-${stageCode}-form`} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 8, fontSize: 11 }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setOpenSections(new Set(schema.map((s) => s.id)))}
              data-testid={`dbw-${stageCode}-expand-all`}
              style={{ fontSize: 11, padding: "4px 10px" }}
            >Expand all</button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setOpenSections(new Set())}
              data-testid={`dbw-${stageCode}-collapse-all`}
              style={{ fontSize: 11, padding: "4px 10px" }}
            >Collapse all</button>
          </div>

          {/* Wave AJ — Cross-field validation banner */}
          {(() => {
            const warns = [];
            const errs = [];
            // A.3 cooling water
            const cws = data.cw_supply_t, cwr = data.cw_return_t;
            if (cws != null && cwr != null && cwr <= cws)
              errs.push("Cooling Water Return T must be greater than Supply T");
            // A.4 ambient
            const tmax = data.max_ambient_t, tmin = data.min_ambient_t, dryb = data.design_dry_bulb;
            if (tmax != null && tmin != null && tmin >= tmax)
              errs.push("Min Ambient T must be less than Max Ambient T");
            if (tmax != null && dryb != null && dryb < tmax)
              warns.push("Design Dry Bulb (Summer) is lower than Max Ambient T");
            // A.1 / A.3 composition tables sum to 100
            const sumPct = (arr, key) => (Array.isArray(arr) ? arr : [])
              .reduce((a, r) => a + (Number(r?.[key]) || 0), 0);
            for (const [name, arr] of [
              ["Feed mol%", { rows: data.feed_composition, key: "mol_pct" }],
              ["Feed wt%",  { rows: data.feed_composition, key: "wt_pct"  }],
              ["Fuel-gas mol%", { rows: data.fuel_gas_composition, key: "mol_pct" }],
            ]) {
              const sum = sumPct(arr.rows, arr.key);
              if (sum > 0 && Math.abs(sum - 100) > 0.5)
                warns.push(`${name} sum is ${sum.toFixed(2)} — should be 100 ± 0.5`);
            }
            if (!errs.length && !warns.length) return null;
            return (
              <div data-testid={`dbw-${stageCode}-xvalid`} style={{
                padding: "10px 12px", marginBottom: 8, borderRadius: 4,
                fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                border: `1px solid ${errs.length ? "#ef4444" : "#eab308"}`,
                background: errs.length ? "rgba(239,68,68,0.08)" : "rgba(234,179,8,0.08)",
                color: errs.length ? "#fca5a5" : "#fde68a",
              }}>
                {errs.map((e, i) => <div key={"e" + i}>✗ {e}</div>)}
                {warns.map((w, i) => <div key={"w" + i}>⚠ {w}</div>)}
              </div>
            );
          })()}

          {schema.map((section) => {
            const isOpen = openSections.has(section.id);
            const filledCount = section.fields.filter((f) => {
              const v = data[f.key];
              return v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
            }).length;
            const total = section.fields.length;
            const toggleSection = () => {
              setOpenSections((cur) => {
                const next = new Set(cur);
                if (next.has(section.id)) next.delete(section.id);
                else next.add(section.id);
                return next;
              });
            };
            return (
              <div
                key={section.id}
                className="pf-card"
                style={{ padding: 0, overflow: "hidden" }}
                data-testid={`dbw-${stageCode}-section-${section.id}`}
              >
                <button
                  type="button"
                  onClick={toggleSection}
                  data-testid={`dbw-${stageCode}-section-${section.id}-toggle`}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    padding: "12px 14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: "var(--pf-text)",
                    fontWeight: 600,
                    fontSize: 13.5,
                    textAlign: "left",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {section.title}
                  </span>
                  <span
                    style={{
                      fontSize: 11, fontWeight: 400,
                      color: filledCount === total ? "var(--pf-ok, #4ade80)" : "var(--pf-text-muted)",
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {filledCount}/{total} filled
                  </span>
                </button>
                {isOpen && (
                  <div
                    style={{
                      padding: "0 14px 14px",
                      borderTop: "1px solid var(--pf-border-muted, var(--pf-border))",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {section.fields.filter((f) => {
                      // Wave AJ — conditional visibility per `DBD_FIELD_DATA_TYPES.md`
                      const c = f.conditional;
                      if (!c) return true;
                      const target = data[c.field];
                      if (c.op === "truthy") return !!target;
                      if (c.op === "==") return target === c.value;
                      if (c.op === "!=") return target !== c.value;
                      return true;
                    }).map((f) => (
                      <FieldRenderer key={f.key} field={f} value={data[f.key]} onChange={update}
                                     locked={locked} stageCode={stageCode}
                                     provenance={provenance[f.key]}
                                     cities={cities}
                                     citiesRich={citiesRich}
                                     busyAction={busyAction}
                                     onUpload={uploadFile}
                                     onRemoveUpload={removeUpload}
                                     apiRoot={apiRoot}
                                     projectId={projectId}
                                     onLocationBlur={(v) => { update(f.key, v); applyLocationDefaults(v); }} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Action row at the end of the accordion. CH.3 — removed
              `position: sticky` because it caused the bar to overlap
              the next form section while scrolling. Bar now sits
              naturally after the last section. */}
          <div className="pf-card"
               data-testid={`dbw-${stageCode}-actions`}
               style={{ padding: 14, display: "flex", justifyContent: "space-between",
                        alignItems: "center", flexWrap: "wrap", gap: 10,
                        marginTop: 8,
                        borderTop: "2px solid var(--pf-accent)",
                        background: "var(--pf-surface)" }}>
            <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>
              {completeness?.critical_missing?.length > 0 ? (
                <span style={{ color: "var(--pf-err)" }}>
                  <AlertTriangle size={12} /> Missing critical fields: {completeness.critical_missing.join(", ")}
                </span>
              ) : (
                <span>All critical fields filled. Ready to run.</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {locked ? (
                <button className="btn btn-ghost" onClick={revise} disabled={busyAction === "revise"} data-testid={`dbw-${stageCode}-revise`}>
                  {busyAction === "revise" ? <Loader2 className="pf-spin" size={13} /> : <Unlock size={13} />} Revise
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => generate()}
                    disabled={!completeness?.can_generate || busyAction === "generate"}
                    data-testid={`stage-run-${stageCode}`}
                    title="Run this stage — generates the document. If a cached AI narrative exists, it is reused at no Capexara Process Industry AI charge."
                    style={{ fontSize: 13, fontWeight: 600, padding: "8px 16px" }}
                  >
                    {busyAction === "generate" ? <><Loader2 className="pf-spin" size={14} /> Running…</> : <><Play size={14} /> Run Stage</>}
                  </button>
                  {narrativeMd ? (
                    <>
                      <button className="btn btn-ghost"
                          onClick={retryRender}
                          disabled={busyAction === "generate"}
                          data-testid={`dbw-${stageCode}-retry-render`}
                          title="Rebuilds the PDF/DOCX from the CACHED narrative — no Capexara Process Industry AI call, no charges."
                          style={{ fontSize: 12 }}>
                        <Download size={13} /> Retry rendering (no charge)
                      </button>
                      <button className="btn btn-ghost"
                          onClick={regenerateWithLLM}
                          disabled={busyAction === "generate"}
                          data-testid={`dbw-${stageCode}-regenerate-llm`}
                          title="Re-calls Capexara Process Industry AI — consumes Capexara Process Industry AI credits."
                          style={{ fontSize: 12 }}>
                        <Sparkles size={13} /> Regenerate with AI (charges)
                      </button>
                    </>
                  ) : null}
                  <button className="btn btn-ghost" onClick={approve} disabled={!completeness?.can_generate || busyAction === "approve"} data-testid={`dbw-${stageCode}-approve`}>
                    {busyAction === "approve" ? <Loader2 className="pf-spin" size={13} /> : <FileCheck size={13} />} Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── GENERATED DOCUMENT TAB ────────────────────────────────────── */}
      {viewMode === "document" && (
        <div data-testid={`dbw-${stageCode}-document`} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {!narrativeMd && !latestDeliverable ? (
            <div className="pf-card" style={{ padding: 24, textAlign: "center", color: "var(--pf-text-muted)" }}>
              No document has been generated yet. Fill the form and click <b>Generate Document</b>.
            </div>
          ) : !narrativeMd && latestDeliverable ? (
            // Wave AL.2 — deliverable exists but AI narrative is empty
            // (LLM returned None on the last attempt). Show downloads + a
            // clear retry CTA instead of the misleading "no document" UI.
            <>
              <NoNarrativeAutoHealBanner
                stageCode={stageCode}
                projectId={projectId}
                busyAction={busyAction}
                onRegenerate={regenerateWithLLM}
                latestDeliverable={latestDeliverable}
              />
              <div className="pf-card" style={{ padding: 12, display: "flex",
                       gap: 10, alignItems: "center", flexWrap: "wrap" }}
                    data-testid={`dbw-${stageCode}-downloads`}>
                <span className="pf-overline">Downloads:</span>
                {["pdf", "docx", "json"].map((fmt) => (
                  <a key={fmt}
                       href={`${apiRoot}/projects/${projectId}/design-basis/document/${stageCode}/${fmt}`}
                       target="_blank" rel="noreferrer"
                       className="btn btn-ghost"
                       data-testid={`dbw-${stageCode}-dl-${fmt}`}
                       style={{ fontSize: 12 }}>
                    <Download size={13} /> {fmt.toUpperCase()}
                  </a>
                ))}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: "var(--pf-text-muted)",
                                  fontFamily: "'JetBrains Mono', monospace" }}>
                  v{latestDeliverable.deliverable?.version} · structured-only
                </span>
              </div>
            </>
          ) : (
            <>
              {/* Downloads strip */}
              <div className="pf-card" style={{ padding: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }} data-testid={`dbw-${stageCode}-downloads`}>
                <span className="pf-overline">Downloads:</span>
                {["pdf", "docx", "json"].map((fmt) => (
                  <a key={fmt}
                     href={`${apiRoot}/projects/${projectId}/design-basis/document/${stageCode}/${fmt}`}
                     target="_blank" rel="noreferrer"
                     className="btn btn-ghost" data-testid={`dbw-${stageCode}-dl-${fmt}`}
                     style={{ fontSize: 12 }}>
                    <Download size={13} /> {fmt.toUpperCase()}
                  </a>
                ))}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                  {(narrativeMd.length / 1000).toFixed(1)}k chars · Capexara Process Industry AI
                </span>
              </div>

              {/* Side-by-side: Markdown viewer · Revise with AI ───────────
                  CH.3 — grid + align-items: stretch lets both columns fill
                  the available vertical space natively (no calc). Revise
                  panel collapses HORIZONTALLY to a thin 36 px strip on
                  the right; expanded = 300–380 px. */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: reviseCollapsed
                    ? "minmax(0, 1fr) 36px"
                    : "minmax(0, 1fr) minmax(300px, 380px)",
                  gap: 12,
                  alignItems: "stretch",
                  height: "calc(100vh - 230px)",
                  minHeight: "55vh",
                  transition: "grid-template-columns 0.22s ease",
                }}
                data-testid={`dbw-${stageCode}-doc-layout`}
              >
                {/* In-page Markdown viewer — fills the grid cell. */}
                <div
                  className="pf-card pf-doc-viewer"
                  data-testid={`dbw-${stageCode}-doc-view`}
                  style={{
                    padding: "24px 28px",
                    height: "100%",
                    overflowY: "auto",
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: "var(--pf-text)",
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {narrativeMd}
                  </ReactMarkdown>
                </div>

                {/* Revise with AI panel — fills full height (align-items:
                    stretch) and collapses horizontally to a thin vertical
                    strip with rotated label + chevron when minimised. */}
                <div
                  className="pf-card"
                  data-testid={`dbw-${stageCode}-revise-card`}
                  style={{
                    padding: 0,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  <button type="button"
                      data-testid={`dbw-${stageCode}-revise-toggle`}
                      onClick={() => setReviseCollapsed((v) => {
                        const n = !v;
                        try { sessionStorage.setItem("rachana.reviseCollapsed", n ? "1" : "0"); } catch {}
                        return n;
                      })}
                      title={reviseCollapsed ? "Expand Revise panel" : "Collapse Revise panel"}
                      style={reviseCollapsed
                        ? { background: "transparent", border: "none",
                            padding: "12px 0", cursor: "pointer",
                            color: "var(--pf-text)",
                            display: "flex", flexDirection: "column",
                            alignItems: "center", gap: 10, width: "100%",
                            height: "100%" }
                        : { background: "transparent", border: "none",
                            padding: "12px 14px", cursor: "pointer",
                            color: "var(--pf-text)",
                            display: "flex", alignItems: "center",
                            justifyContent: "space-between", width: "100%" }}>
                    {reviseCollapsed ? (
                      <>
                        <ChevronLeft size={14} />
                        <span className="pf-overline" style={{
                              writingMode: "vertical-rl", transform: "rotate(180deg)",
                              letterSpacing: 1.2, margin: 0,
                              display: "flex", alignItems: "center", gap: 6 }}>
                          <Wand2 size={11} /> REVISE WITH AI
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="pf-overline" style={{ display: "flex",
                                      alignItems: "center", gap: 6, margin: 0 }}>
                          <Wand2 size={11} /> REVISE WITH AI
                        </span>
                        <ChevronRight size={13}/>
                      </>
                    )}
                  </button>
                  {!reviseCollapsed && (
                  <div style={{ padding: "0 14px 14px",
                                  flex: 1, minHeight: 0, overflowY: "auto",
                                  display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>
                    Describe the change. Optionally pick a section to scope the edit and/or attach screenshots for visual context. Capexara Process Industry AI rewrites only the targeted clauses; the rest stays verbatim.
                  </div>

                  {/* Section reference dropdown */}
                  {narrativeSections.length > 0 && (
                    <div>
                      <label style={{ fontSize: 10, color: "var(--pf-text-dim)", display: "block", marginBottom: 4, letterSpacing: "0.08em" }}>TARGET SECTION (optional)</label>
                      <select
                        className="pf-input"
                        value={reviseSectionRef}
                        onChange={(e) => setReviseSectionRef(e.target.value)}
                        data-testid={`dbw-${stageCode}-revise-section`}
                        style={{ width: "100%", fontSize: 12 }}
                      >
                        <option value="">— whole document —</option>
                        {narrativeSections.map((s, i) => (
                          <option key={i} value={s.title}>
                            {"".padStart((s.level - 1) * 2, " ")}{s.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <textarea
                    className="pf-input"
                    placeholder="Your revision instruction…"
                    value={reviseInstruction}
                    onChange={(e) => setReviseInstruction(e.target.value)}
                    disabled={busyAction === "revise"}
                    data-testid={`dbw-${stageCode}-revise-input`}
                    style={{ minHeight: 90, fontSize: 12.5, resize: "vertical" }}
                  />

                  {/* Screenshot attachments */}
                  <div>
                    <label style={{ fontSize: 10, color: "var(--pf-text-dim)", display: "block", marginBottom: 4, letterSpacing: "0.08em" }}>REFERENCE SCREENSHOTS (optional)</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {reviseScreenshots.map((s) => (
                        <div key={s.id} data-testid={`dbw-${stageCode}-revise-shot-${s.id}`}
                             style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, background: "var(--pf-surface-2)", padding: 6, border: "1px solid var(--pf-border)" }}>
                          <a href={`${apiRoot}${s.url}`} target="_blank" rel="noreferrer"
                             style={{ flex: 1, color: "var(--pf-spark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.filename || s.id}
                          </a>
                          <span style={{ color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                            {Math.round((s.size_bytes || 0) / 1024)} KB
                          </span>
                          <button type="button"
                                  onClick={() => setReviseScreenshots((prev) => prev.filter((x) => x.id !== s.id))}
                                  data-testid={`dbw-${stageCode}-revise-shot-rm-${s.id}`}
                                  style={{ background: "transparent", border: "none", color: "var(--pf-text-muted)", cursor: "pointer", padding: 0 }}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <label
                        data-testid={`dbw-${stageCode}-revise-shot-add`}
                        style={{
                          fontSize: 11, cursor: "pointer",
                          color: "var(--pf-text-muted)",
                          border: "1px dashed var(--pf-border)",
                          padding: "8px 10px",
                          textAlign: "center",
                        }}
                      >
                        <Plus size={11} /> Attach screenshot
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            for (const f of files) await uploadReviseScreenshot(f);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={reviseWithAI}
                    disabled={locked || busyAction === "revise" || !reviseInstruction.trim()}
                    data-testid={`dbw-${stageCode}-revise-btn`}
                    style={{ fontSize: 12, fontWeight: 600, alignSelf: "stretch", justifyContent: "center", marginTop: 4 }}
                  >
                    {busyAction === "revise"
                      ? <><Loader2 className="pf-spin" size={13} /> Revising…</>
                      : <><Wand2 size={13} /> Revise with AI</>}
                  </button>

                  {/* Version history */}
                  {versions.length > 0 && (
                    <div style={{ borderTop: "1px dashed var(--pf-border-muted, var(--pf-border))", paddingTop: 10, marginTop: 6 }}>
                      <div className="pf-overline" style={{ marginBottom: 6 }}>VERSION HISTORY</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, maxHeight: 180, overflowY: "auto" }}>
                        {versions.map((v) => (
                          <div key={v.id} data-testid={`dbw-${stageCode}-ver-${v.version}`}
                               style={{ padding: 6, border: "1px solid var(--pf-border)", background: v.is_latest ? "var(--pf-surface-2)" : "transparent", display: "flex", flexDirection: "column", gap: 2 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: v.is_latest ? "var(--pf-ok, #4ade80)" : "var(--pf-text)" }}>
                                v{v.version}{v.is_latest && " (active)"}
                              </span>
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: "var(--pf-text-dim)" }}>
                                {v.created_at?.slice(0, 19).replace("T", " ")}
                              </span>
                            </div>
                            <div style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>{v.summary}</div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2 }}>
                              <span style={{ fontSize: 9, color: "var(--pf-text-dim)", fontFamily: "'JetBrains Mono', monospace" }}>
                                {(v.narrative_chars / 1000).toFixed(1)}k chars
                              </span>
                              {!v.is_latest && (
                                <button type="button"
                                        onClick={() => restoreVersion(v.id)}
                                        disabled={locked || busyAction === "restore"}
                                        data-testid={`dbw-${stageCode}-restore-${v.version}`}
                                        className="btn btn-ghost"
                                        style={{ fontSize: 9, padding: "1px 6px" }}>
                                  Restore
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  </div>
                  )}
                </div>
              </div>

              {/* Quick back-to-form shortcut */}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setViewMode("form")}
                  data-testid={`dbw-${stageCode}-back-to-form`}
                  style={{ fontSize: 11 }}
                >
                  ← Back to Form
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


/* ─── Field renderer — switches on field.type ─────────────────────────── */
function FieldRenderer({ field, value, onChange, locked, stageCode, onLocationBlur,
                        provenance, cities, citiesRich, busyAction, onUpload, onRemoveUpload,
                        apiRoot, projectId }) {
  const f = field;
  // Wave V.7.2 — per-user request: NO pre-filled values. We ignore f.default
  // on the client so every wizard starts empty for the engineer. The backend
  // still carries defaults for reference data (e.g. units, ambient climate
  // defaults) but they are never written into the user's answers unless the
  // user explicitly picks that value.
  const v = value;
  const tid = `dbw-${stageCode}-f-${f.key}`;
  // Provenance dot — green = AI-extracted, orange = auto-filled from DB,
  // gray = unchanged default (no dot rendered for plain user-entered values).
  const provColor = provenance === "ai_extracted" ? "#22c55e"
                  : provenance === "auto_filled"   ? "#fb923c"
                  : provenance === "default"       ? "#94a3b8"
                  : null;
  const provDot = provColor ? (
    <span title={`Source: ${provenance}`}
          data-testid={`${tid}-prov`}
          data-prov={provenance}
          style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                   background: provColor, marginLeft: 6 }} />
  ) : null;

  const labelEl = (
    <label style={{ display: "block", fontSize: 11, color: "var(--pf-text-muted)", marginBottom: 4, fontWeight: 500 }}>
      {f.label}{f.required ? <span style={{ color: "var(--pf-err)" }}> *</span> : null}
      {f.unit ? <span style={{ color: "var(--pf-text-dim)", marginLeft: 6 }}>({f.unit})</span> : null}
      {provDot}
    </label>
  );
  const helpEl = f.help ? <div style={{ fontSize: 10, color: "var(--pf-text-dim)", marginTop: 2 }}>{f.help}</div> : null;
  const baseInput = { className: "pf-input", style: { width: "100%" }, "data-testid": tid, disabled: locked };
  const provBorder = provColor
    ? { borderLeft: `3px solid ${provColor}`, paddingLeft: 6 }
    : {};

  // Wave AJ — server-defined validation messages per `DBD_FIELD_DATA_TYPES.md`
  const validationError = (() => {
    if (locked) return null;
    const empty = v === null || v === undefined || v === "";
    if (f.required && empty) return null;   // required-not-filled is shown via the * marker, not as red text
    if (empty) return null;
    if (f.type === "number" && typeof v === "number") {
      if (typeof f.min === "number" && v < f.min) return `Min ${f.min}${f.unit ? " " + f.unit : ""}`;
      if (typeof f.max === "number" && v > f.max) return `Max ${f.max}${f.unit ? " " + f.unit : ""}`;
      if (f.data_type === "integer" && !Number.isInteger(v)) return "Must be a whole number";
    }
    if ((f.type === "text" || f.type === "textarea") && typeof v === "string") {
      if (typeof f.min_chars === "number" && v.length < f.min_chars) return `Min ${f.min_chars} chars`;
      if (typeof f.max_chars === "number" && v.length > f.max_chars) return `Max ${f.max_chars} chars`;
      if (f.pattern) {
        try {
          if (!new RegExp(f.pattern).test(v)) return "Invalid format";
        } catch { /* invalid regex from server — ignore */ }
      }
    }
    return null;
  })();
  const errorEl = validationError ? (
    <div data-testid={`${tid}-err`} style={{ fontSize: 10, color: "#ef4444", marginTop: 2 }}>{validationError}</div>
  ) : null;

  if (f.type === "select") {
    // Wave V.7.2 — every dropdown now supports a "Custom…" escape hatch so
    // the user can enter a value not in the curated list. When the current
    // value is something outside f.options, we show the free-text entry
    // automatically so typed-in customisations round-trip on save/reload.
    const optionsList = f.options || [];
    const OTHER = "__custom__";
    // Coerce v to a string before list-membership / option-render so a
    // bad save (object / array) never crashes the dropdown.
    const vStr = (typeof v === "string" || typeof v === "number")
      ? String(v) : "";
    const isCustom = vStr !== "" && !optionsList.includes(vStr);
    const selectValue = isCustom ? OTHER : (vStr ?? "");
    return (
      <div style={provBorder}>{labelEl}
        <select
          {...baseInput}
          value={selectValue}
          onChange={(e) => {
            const next = e.target.value;
            if (next === OTHER) {
              // Entering custom mode — clear so the text input renders blank.
              onChange(f.key, "");
            } else {
              onChange(f.key, next);
            }
          }}
        >
          <option value="">— select —</option>
          {optionsList.map((o) => <option key={String(o)} value={o}>{o}</option>)}
          <option value={OTHER}>Other / custom…</option>
        </select>
        {(isCustom || selectValue === OTHER) && (
          <input
            type="text"
            className="pf-input"
            disabled={locked}
            data-testid={`${tid}-custom`}
            placeholder={`Enter a custom ${String(f.label || "value").toLowerCase()}…`}
            value={isCustom ? vStr : ""}
            onChange={(e) => onChange(f.key, e.target.value)}
            style={{ width: "100%", marginTop: 6 }}
          />
        )}
        {helpEl}
      </div>
    );
  }
  if (f.type === "checkbox") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 18 }}>
        <input type="checkbox" data-testid={tid} disabled={locked}
               checked={!!v} onChange={(e) => onChange(f.key, e.target.checked)} />
        <label style={{ fontSize: 12.5 }}>{f.label}{f.required ? " *" : ""}{provDot}</label>
      </div>
    );
  }
  if (f.type === "checklist") {
    const arr = Array.isArray(v) ? v : [];
    return (
      <div style={provBorder}>{labelEl}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(f.options || []).map((o) => {
            const on = arr.includes(o);
            return (
              <button key={o} type="button" disabled={locked}
                data-testid={`${tid}-${String(o).replace(/\W+/g, "_")}`}
                onClick={() => onChange(f.key, on ? arr.filter((x) => x !== o) : [...arr, o])}
                style={{
                  fontSize: 11, padding: "4px 8px",
                  border: "1px solid " + (on ? "var(--pf-spark)" : "var(--pf-border)"),
                  borderRadius: 12, cursor: locked ? "default" : "pointer",
                  background: on ? "var(--pf-spark-soft, rgba(96, 165, 250, 0.1))" : "transparent",
                  color: on ? "var(--pf-spark)" : "var(--pf-text-muted)",
                }}>{o}</button>
            );
          })}
        </div>{helpEl}</div>
    );
  }
  if (f.type === "textarea") {
    return (
      <div style={{ gridColumn: "1 / -1", ...provBorder }}>{labelEl}
        <textarea {...baseInput} className="pf-input pf-textarea"
                  rows={3}
                  maxLength={f.max_chars || undefined}
                  placeholder={f.placeholder || undefined}
                  value={v ?? ""} onChange={(e) => onChange(f.key, e.target.value)} />{helpEl}{errorEl}</div>
    );
  }
  if (f.type === "table") {
    return <TableField field={f} value={v} onChange={(rows) => onChange(f.key, rows)} locked={locked} tid={tid} />;
  }
  if (f.type === "file" || f.type === "file_multi") {
    const multi = f.type === "file_multi";
    const items = multi
      ? (Array.isArray(v) ? v : [])
      : (v && typeof v === "object" && v.gridfs_id ? [v] : []);
    const inputId = `${tid}-input`;
    const isBusy = busyAction === `upload-${f.key}`;
    return (
      <div style={{ gridColumn: "1 / -1", ...provBorder }}>{labelEl}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.length > 0 ? items.map((it) => (
            <div key={it.gridfs_id} data-testid={`${tid}-row-${it.gridfs_id}`}
                 style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11,
                          padding: "4px 8px", border: "1px solid var(--pf-border)",
                          borderRadius: 4, background: "var(--pf-surface)" }}>
              <FileText size={12} color="var(--pf-text-muted)" />
              <a href={`${apiRoot}/projects/${projectId}/design-basis/upload/${stageCode}/${f.key}/${it.gridfs_id}/download`}
                 target="_blank" rel="noreferrer"
                 style={{ flex: 1, color: "var(--pf-text)", textDecoration: "none" }}>
                {it.name} <span style={{ color: "var(--pf-text-dim)" }}>({Math.round((it.size || 0) / 1024)} KB)</span>
              </a>
              {!locked && (
                <button type="button" onClick={() => onRemoveUpload(f.key, it.gridfs_id)}
                        disabled={busyAction === `remove-${it.gridfs_id}`}
                        data-testid={`${tid}-remove-${it.gridfs_id}`}
                        style={{ background: "transparent", border: "none",
                                 color: "var(--pf-err)", cursor: "pointer" }}>
                  <X size={11} />
                </button>
              )}
            </div>
          )) : (
            <div style={{ fontSize: 11, color: "var(--pf-text-dim)" }}>No files uploaded yet.</div>
          )}
          {!locked && (multi || items.length === 0) && (
            <div>
              <input type="file" id={inputId}
                     data-testid={`${tid}-input`}
                     accept={f.accept || ""}
                     onChange={(e) => {
                       const file = e.target.files?.[0];
                       if (file) onUpload(f.key, file);
                       e.target.value = "";
                     }}
                     style={{ display: "none" }} />
              <label htmlFor={inputId} className="btn btn-ghost"
                     data-testid={`${tid}-upload-btn`}
                     style={{ fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                {isBusy ? <Loader2 className="pf-spin" size={12} /> : <Plus size={12} />}
                {isBusy ? "Uploading…" : (multi ? "Add file" : "Upload file")}
              </label>
              {f.accept ? <span style={{ fontSize: 10, color: "var(--pf-text-dim)", marginLeft: 8 }}>
                Accepts: {f.accept}
              </span> : null}
            </div>
          )}
        </div>{helpEl}
      </div>
    );
  }
  if (f.type === "date") {
    return (
      <div style={provBorder}>{labelEl}
        <input type="date" {...baseInput} value={v ?? ""} onChange={(e) => onChange(f.key, e.target.value)} />{helpEl}</div>
    );
  }
  if (f.type === "number") {
    const numStep = f.step != null ? f.step : (f.data_type === "integer" ? 1 : "any");
    return (
      <div style={provBorder}>{labelEl}
        <input type="number"
               step={numStep}
               min={f.min != null ? f.min : undefined}
               max={f.max != null ? f.max : undefined}
               placeholder={f.placeholder || undefined}
               {...baseInput} value={v ?? ""}
               onChange={(e) => onChange(f.key, e.target.value === "" ? null : Number(e.target.value))} />{helpEl}{errorEl}</div>
    );
  }
  if (f.type === "location") {
    return (
      <div style={{ gridColumn: "1 / -1", ...provBorder }}>
        {labelEl}
        <CityCombobox
          value={v}
          cities={cities || []}
          citiesRich={citiesRich || []}
          projectId={projectId}
          tid={tid}
          onChange={(val) => onChange(f.key, val)}
          onCommit={(val) => onLocationBlur && onLocationBlur(val)}
        />
        <div style={{ fontSize: 10, color: "var(--pf-text-dim)", marginTop: 2 }}>
          {(citiesRich || []).length > 0
            ? `${(citiesRich || []).length} cities in database across IN / US / UK / EU / AU / NZ / AE / SA / ME / SG — coordinates, seismic zone, wind speed & climate auto-fill on selection. Anything not in the list is resolved via OpenStreetMap.`
            : "Climate, seismic and wind defaults will auto-fill on selection."}
        </div>
        {helpEl}
      </div>
    );
  }
  // default text — coerce non-string values defensively so legacy
  // saves (e.g. an object {lat,lon} accidentally stored in a text
  // field) don't crash with "[object Object]" or break .toLowerCase
  // anywhere downstream.
  const textValue = (() => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (Array.isArray(v)) return v.map(String).join(", ");
    if (typeof v === "object") {
      // Prefer common lat/lon shape → "lat, lon"
      if (typeof v.lat === "number" && typeof v.lon === "number") {
        return `${v.lat}, ${v.lon}`;
      }
      if (typeof v.lat === "number" && typeof v.lng === "number") {
        return `${v.lat}, ${v.lng}`;
      }
      return String(v.city || v.name || v.value || v.label
                       || JSON.stringify(v));
    }
    return String(v);
  })();
  return (
    <div style={provBorder}>{labelEl}
      <input type="text" {...baseInput} value={textValue}
             maxLength={f.max_chars || undefined}
             pattern={f.pattern || undefined}
             placeholder={f.placeholder || undefined}
             onChange={(e) => onChange(f.key, e.target.value)} />{helpEl}{errorEl}</div>
  );
}


/* ─── Editable table field ────────────────────────────────────────────── */
function TableField({ field, value, onChange, locked, tid }) {
  const cols = field.columns || [];
  const rows = Array.isArray(value) ? value : [];
  const update = (idx, key, v) => {
    const copy = rows.map((r, i) => i === idx ? { ...r, [key]: v } : r);
    onChange(copy);
  };
  const addRow = () => {
    const empty = Object.fromEntries(cols.map((c) => [c.key, ""]));
    onChange([...rows, empty]);
  };
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <label style={{ display: "block", fontSize: 11, color: "var(--pf-text-muted)", marginBottom: 4, fontWeight: 500 }}>
        {field.label}{field.required ? " *" : ""}
      </label>
      <div style={{ overflowX: "auto", border: "1px solid var(--pf-border)", borderRadius: 4 }} data-testid={tid}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--pf-surface)" }}>
              {cols.map((c) => <th key={c.key} style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid var(--pf-border)" }}>{c.label}</th>)}
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {cols.map((c) => (
                  <td key={c.key} style={{ padding: 4, borderBottom: "1px solid var(--pf-border)" }}>
                    <input type="text" className="pf-input"
                           data-testid={`${tid}-r${idx}-${c.key}`}
                           style={{ width: "100%", border: "none", background: "transparent", padding: "4px 6px" }}
                           value={row[c.key] ?? ""} disabled={locked}
                           onChange={(e) => update(idx, c.key, e.target.value)} />
                  </td>
                ))}
                <td style={{ textAlign: "center" }}>
                  {!locked && (
                    <button onClick={() => removeRow(idx)} type="button"
                            style={{ background: "transparent", border: "none", color: "var(--pf-text-dim)", cursor: "pointer" }}
                            data-testid={`${tid}-r${idx}-remove`}>
                      <X size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!locked && (
        <button type="button" onClick={addRow} className="btn btn-ghost"
                style={{ fontSize: 11, marginTop: 6 }} data-testid={`${tid}-add`}>
          <Plus size={12} /> Add row
        </button>
      )}
    </div>
  );
}


/* ─── CityCombobox ────────────────────────────────────────────────────────
 * Click-to-open, type-to-search location picker.
 *
 *  • Primary source: local rich DB (~320 cities across all supported
 *    countries) — searches across name, state, country_code.
 *  • Fallback: OpenStreetMap Nominatim (via `/design-basis/geocode?q=`)
 *    — kicks in when the user types something not in the local DB.
 *    Debounced 350ms to respect the free service.
 *  • Each pick fires `onCommit(record)` with the FULL record so the
 *    parent can auto-fill coordinates + seismic + wind + climate.
 */
function CityCombobox({ value, cities, citiesRich, projectId, tid,
                                onChange, onCommit }) {
  const [open, setOpen] = React.useState(false);
  const _s = (x) => {
    if (typeof x === "string") return x;
    if (x && typeof x === "object" && !Array.isArray(x)) {
      return String(x.city || x.name || x.value || x.label || "");
    }
    return "";
  };
  const [q, setQ] = React.useState(_s(value));
  const [highlight, setHighlight] = React.useState(0);
  const [remoteResults, setRemoteResults] = React.useState([]);
  const [remoteLoading, setRemoteLoading] = React.useState(false);
  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => { setQ(_s(value)); }, [value]);

  // Click-outside closes
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Local search — matches across name, state, country_code
  const localHits = React.useMemo(() => {
    const ql = _s(q).toLowerCase().trim();
    const rich = Array.isArray(citiesRich) ? citiesRich : [];
    if (rich.length === 0) {
      // Fallback to plain city list if rich not loaded yet
      const flat = (Array.isArray(cities) ? cities : []).map(_s).filter(Boolean);
      return (ql ? flat.filter((c) => c.toLowerCase().includes(ql)) : flat)
        .slice(0, 30)
        .map((c) => ({ name: c, state: "", country_code: "", source: "local" }));
    }
    if (!ql) return rich.slice(0, 30);
    return rich.filter((c) => {
      const hay = `${c.name} ${c.state || ""} ${c.country_code || ""}`.toLowerCase();
      return hay.includes(ql);
    }).slice(0, 30);
  }, [q, cities, citiesRich]);

  // Debounced Nominatim fallback — only when local hits are few and
  // the user has typed at least 3 chars.
  React.useEffect(() => {
    const ql = _s(q).trim();
    if (ql.length < 3) { setRemoteResults([]); return; }
    if (!projectId) return;
    // Skip remote lookup if local has plenty of matches
    if (localHits.length >= 5) { setRemoteResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setRemoteLoading(true);
        const r = await api.get(
          `/projects/${projectId}/design-basis/geocode`,
          { params: { q: ql } });
        if (cancelled) return;
        const results = (r.data?.results || []).filter(
          (x) => x.source === "nominatim");
        setRemoteResults(results.slice(0, 8));
      } catch {
        if (!cancelled) setRemoteResults([]);
      } finally {
        if (!cancelled) setRemoteLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, projectId, localHits.length]);

  const combined = React.useMemo(() => {
    // Local first, then remote (dedupe by name+state)
    const seen = new Set(localHits.map(
      (c) => `${(c.name || "").toLowerCase()}|${(c.state || "").toLowerCase()}`));
    const remote = remoteResults.filter((c) => !seen.has(
      `${(c.name || "").toLowerCase()}|${(c.state || "").toLowerCase()}`));
    return [...localHits, ...remote];
  }, [localHits, remoteResults]);

  const choose = (rec) => {
    const name = rec && rec.name ? rec.name : _s(rec);
    setQ(name);
    onChange && onChange(name);
    setOpen(false);
    // Emit the full record when we have one — parent uses this to fetch
    // climate + seismic + wind defaults + coordinates all at once.
    onCommit && onCommit(rec && typeof rec === "object" ? rec : name);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault(); setOpen(true);
      setHighlight((h) => Math.min(combined.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && combined[highlight]) choose(combined[highlight]);
      else { onCommit && onCommit(q); setOpen(false); }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Country flag emoji — purely decorative
  const countryFlag = (cc) => ({
    IN: "🇮🇳", US: "🇺🇸", UK: "🇬🇧", GB: "🇬🇧", EU: "🇪🇺",
    AU: "🇦🇺", NZ: "🇳🇿", AE: "🇦🇪", SA: "🇸🇦", ME: "🌍",
    SG: "🇸🇬",
  })[cc] || "🌐";

  // Grouped by country for readability
  const grouped = React.useMemo(() => {
    const g = {};
    for (const c of combined) {
      const cc = c.country_code || "OTHER";
      (g[cc] = g[cc] || []).push(c);
    }
    return g;
  }, [combined]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "stretch", gap: 0 }}>
        <input
          ref={inputRef}
          type="text"
          className="pf-input"
          data-testid={tid}
          value={q}
          onChange={(e) => { setQ(e.target.value); onChange && onChange(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKey}
          onBlur={() => {
            setTimeout(() => {
              if (!wrapRef.current?.contains(document.activeElement)) {
                setOpen(false);
                onCommit && onCommit(q);
              }
            }, 150);
          }}
          placeholder="Type a city — e.g. Jamnagar, Houston, Doha, Rotterdam…"
          style={{ flex: 1, paddingRight: 28 }}
        />
        <button
          type="button"
          data-testid={`${tid}-toggle`}
          onMouseDown={(e) => {
            e.preventDefault();
            setOpen((o) => !o);
            inputRef.current?.focus();
          }}
          style={{
            position: "absolute", right: 6, top: "50%",
            transform: "translateY(-50%)",
            background: "transparent", border: 0, cursor: "pointer",
            color: "var(--pf-text-muted)", padding: 4,
            display: "flex", alignItems: "center",
          }}
          aria-label="Toggle city list"
          title="Show all cities"
        >
          <ChevronDown size={16} style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 120ms ease",
          }} />
        </button>
      </div>
      {open && (
        <div
          data-testid={`${tid}-list`}
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            zIndex: 1000,
            maxHeight: 340, overflowY: "auto",
            background: "#0f172a",
            border: "1px solid rgba(99,102,241,0.4)",
            borderRadius: 6,
            boxShadow: "0 12px 32px rgba(0,0,0,0.7)",
            backdropFilter: "blur(8px)",
          }}
        >
          {combined.length === 0 && !remoteLoading ? (
            <div style={{ padding: "10px 12px", fontSize: 12,
                            color: "var(--pf-text-muted)" }}>
              No matches. Press Enter to use “{q}” anyway.
            </div>
          ) : combined.length === 0 && remoteLoading ? (
            <div style={{ padding: "10px 12px", fontSize: 12,
                            color: "var(--pf-text-muted)" }}>
              Searching OpenStreetMap for “{q}”…
            </div>
          ) : (
            Object.entries(grouped).map(([cc, entries]) => (
              <div key={cc}>
                <div style={{
                  padding: "6px 12px", fontSize: 10, letterSpacing: "0.08em",
                  color: "#64748b", background: "rgba(255,255,255,0.03)",
                  textTransform: "uppercase", fontWeight: 600,
                }}>
                  {countryFlag(cc)}&nbsp;{cc}
                </div>
                {entries.map((c) => {
                  const globalIdx = combined.indexOf(c);
                  return (
                    <div
                      key={`${c.name}-${c.state || ""}-${cc}-${globalIdx}`}
                      data-testid={`${tid}-opt-${(c.name || "").replace(/[^a-zA-Z0-9]/g, "_")}`}
                      onMouseDown={(e) => { e.preventDefault(); choose(c); }}
                      onMouseEnter={() => setHighlight(globalIdx)}
                      style={{
                        padding: "8px 12px", fontSize: 13, cursor: "pointer",
                        background: globalIdx === highlight ? "rgba(99,102,241,0.18)" : "transparent",
                        color: "#e2e8f0",
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        display: "flex", justifyContent: "space-between",
                        alignItems: "center", gap: 8,
                      }}
                    >
                      <span>
                        <span style={{ fontWeight: 500 }}>{c.name}</span>
                        {c.state ? <span style={{ opacity: 0.65 }}>, {c.state}</span> : null}
                      </span>
                      <span style={{ fontSize: 10, opacity: 0.55, fontFamily: "'JetBrains Mono', monospace" }}>
                        {c.source === "nominatim" ? "OSM" :
                          (c.seismic_zone ? `SZ ${c.seismic_zone}` : "")}
                        {c.basic_wind_speed ? `  •  ${c.basic_wind_speed} m/s` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))
          )}
          {remoteLoading && combined.length > 0 && (
            <div style={{ padding: "6px 12px", fontSize: 10,
                            color: "#64748b",
                            borderTop: "1px solid rgba(255,255,255,0.05)" }}>
              …also searching OpenStreetMap
            </div>
          )}
        </div>
      )}
      {/* Mini map preview after a city is picked */}
      {q && q.trim() && (() => {
        const rec = (citiesRich || []).find((c) =>
          (c.name || "").toLowerCase() === q.trim().toLowerCase());
        if (!rec || !rec.lat) return null;
        const embedSrc =
          `https://www.openstreetmap.org/export/embed.html?bbox=` +
          `${rec.lon - 0.2},${rec.lat - 0.15},${rec.lon + 0.2},${rec.lat + 0.15}` +
          `&layer=mapnik&marker=${rec.lat},${rec.lon}`;
        return (
          <div style={{ marginTop: 8, borderRadius: 6, overflow: "hidden",
                            border: "1px solid var(--pf-border)" }}
                 data-testid={`${tid}-map`}>
            <iframe
              title={`Map of ${rec.name}`}
              width="100%" height="180"
              frameBorder="0" scrolling="no"
              src={embedSrc}
              style={{ display: "block", background: "#0b1120" }}
            />
            <div style={{ fontSize: 10, padding: "4px 8px",
                              color: "var(--pf-text-muted)",
                              background: "rgba(0,0,0,0.35)",
                              fontFamily: "'JetBrains Mono', monospace" }}>
              {rec.name}, {rec.state} ({rec.country_code}) —
              {" "}Lat {Number(rec.lat).toFixed(4)}, Lon {Number(rec.lon).toFixed(4)}
              {rec.seismic_zone ? ` · Seismic ${rec.seismic_zone}` : ""}
              {rec.basic_wind_speed ? ` · Wind ${rec.basic_wind_speed} m/s` : ""}
            </div>
          </div>
        );
      })()}
    </div>
  );
}



/* ─── NoNarrativeAutoHealBanner ────────────────────────────────────────
 * Shown when a deliverable exists on disk but the cached AI narrative is
 * empty (last LLM attempt failed / rate-limited / hit a transient). Now
 * self-heals: silently kicks off a fresh `POST /generate-document?force`
 * on mount and polls the status endpoint so the user sees a live
 * progress readout instead of being stranded with the confusing
 * "AI NARRATIVE UNAVAILABLE" wall.
 */
function NoNarrativeAutoHealBanner({ stageCode, projectId, busyAction,
                                                   onRegenerate, latestDeliverable }) {
  const [jobId, setJobId] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [step, setStep] = React.useState("");
  const [ok, setOk] = React.useState(0);
  const [failed, setFailed] = React.useState(0);
  const [error, setError] = React.useState(null);
  const [autoStarted, setAutoStarted] = React.useState(false);

  const kickOff = React.useCallback(async () => {
    try {
      setError(null);
      setAutoStarted(true);
      const r = await api.post(
        `/projects/${projectId}/design-basis/generate-document/${stageCode}?force_regenerate=true`,
        {});
      const jid = r.data?.job_id;
      setJobId(jid);
      setStatus("running");
      setStep(r.data?.step || "queued");
    } catch (e) {
      setStatus("failed");
      setError(e?.response?.data?.detail || String(e?.message || e));
      setAutoStarted(false);
    }
  }, [projectId, stageCode]);

  React.useEffect(() => {
    if (!autoStarted) kickOff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!jobId || status !== "running") return undefined;
    const timer = setInterval(async () => {
      try {
        const r = await api.get(
          `/projects/${projectId}/design-basis/generate-document/${stageCode}/status/${jobId}`);
        const d = r.data || {};
        setStatus(d.status);
        setStep(d.step || step);
        if (typeof d.ok_section_count === "number") setOk(d.ok_section_count);
        if (typeof d.failed_section_count === "number") setFailed(d.failed_section_count);
        if (d.error) setError(d.error);
        if (d.status === "complete") {
          window.location.reload();
        }
      } catch { /* keep polling */ }
    }, 6000);
    return () => clearInterval(timer);
  }, [jobId, status, projectId, stageCode, step]);

  const stripe = status === "failed" ? "#ef4444"
                     : status === "complete" ? "#10b981" : "#6366f1";
  const label = status === "running" ? "AUTO-REGENERATING WITH CAPEXARA AI"
                    : status === "complete" ? "REGENERATED — REFRESHING"
                    : status === "failed" ? "AUTO-REGENERATION FAILED"
                    : "STARTING AUTO-REGENERATION";

  return (
    <div className="pf-card" data-testid={`dbw-${stageCode}-deliv-no-narrative`}
          style={{ padding: 16, border: `1px solid ${stripe}`,
                    background: `${stripe}12` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
                       marginBottom: 8 }}>
        {status === "running" ? <Loader2 className="pf-spin" size={14}
                                                     style={{ color: stripe }} />
          : <Sparkles size={14} style={{ color: stripe }} />}
        <span style={{ color: stripe, fontWeight: 700, fontSize: 13,
                          letterSpacing: 0.5 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 13, color: "var(--pf-text)", lineHeight: 1.55 }}>
        {status === "failed" ? (
          <>
            The previous AI narrative attempt failed and the automatic
            retry also could not complete
            {error ? <> — <code style={{ fontSize: 11 }}>{String(error).slice(0, 200)}</code></> : ""}.
            Your form data is safe. Click <b>Try Again</b> below, or use
            the <b>Regenerate with AI</b> button in the header to retry
            with a fresh session.
          </>
        ) : status === "complete" ? (
          <>The AI narrative was regenerated successfully. Reloading…</>
        ) : (
          <>
            Deliverable <b>v{latestDeliverable?.deliverable?.version}</b> was
            rendered from your form data but the AI narrative was missing
            (previous LLM attempt timed out or was rate-limited).
            <br />We&apos;re silently regenerating it now — this typically
            takes <b>3–5 minutes</b> for an 11-section document. Feel
            free to leave this tab open; the page will refresh
            automatically when the fresh narrative is ready. Your PDF /
            DOCX / JSON are already downloadable below in the meantime.
            {ok > 0 || failed > 0 ? (
              <div style={{ marginTop: 8, fontSize: 12,
                                color: "var(--pf-text-muted)",
                                fontFamily: "'JetBrains Mono', monospace" }}>
                Progress: {step} · ok {ok} · failed {failed}
              </div>
            ) : null}
          </>
        )}
      </div>
      {(status === "failed") && (
        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button className="btn btn-primary" onClick={kickOff}
                    disabled={busyAction === "generate"}
                    data-testid={`dbw-${stageCode}-autoheal-retry`}
                    style={{ fontSize: 12 }}>
            <Sparkles size={13} /> Try Again
          </button>
          <button className="btn btn-ghost" onClick={onRegenerate}
                    disabled={busyAction === "generate"}
                    style={{ fontSize: 12 }}>
            Manual Regenerate
          </button>
        </div>
      )}
    </div>
  );
}
